import React, {
    MutableRefObject, ReactElement, useRef, useState,
    useEffect,
    ComponentType,
} from "react";
import ReactSelect, {
    CSSObjectWithLabel, NonceProvider, OnChangeValue, SelectInstance as BaseSelectType,
    Props as SelectProps,
    GroupBase, PropsValue, MultiValueProps,
} from "react-select";
import {
    SortableContainer, arrayMove, SortableContainerProps,
} from "react-sortable-hoc";
import Createable from "react-select/creatable";
import type { FilterOptionOption } from "react-select/dist/declarations/src/filters";
import SortableSelectItem from "./SortableSelectItem";

export interface Option<T=never> {
    label: string | ReactElement;
    value: string;
    data?: T
}
export interface OptionWithChildren<T=never> {
    label: string | ReactElement;
    options: Option<T>[];
}

export interface Props<T, TMulti extends boolean = false> {
    placeholder?: string;
    isMulti?: TMulti;
    options: OptionWithChildren<T>[] | Option<T>[];
    value?: (TMulti extends true ? Option<T>[] : Option<T>) | null;
    onChange?: (options: (TMulti extends true ? readonly Option<T>[] : Option<T>)) => void;
    onBlur?: () => void;
    isSortable?: boolean;
    minRequired?: number;
    className?: string;
    isClearable?: boolean;
    onKeyDown?: (event: React.KeyboardEvent<HTMLElement>) => void;
    filterOption?: (item: T, input: string) => boolean | undefined;
}

export interface SelectInstanceHandler {
    blur: () => void;
    focus: () => void;
}

function getCount(value: unknown[] | unknown | undefined): number {
    if (Array.isArray(value)) {
        return value.length;
    }
    return value ? 1 : 0;
}

const ForwardSelect = React.forwardRef(<T, TMulti extends boolean = false>(
    props: Props<T, TMulti>,
    ref: React.ForwardedRef<SelectInstanceHandler>,
): ReactElement => {
    type SelectInstance = BaseSelectType<Option<T>, TMulti>;
    const [selectedCount, setSelectedCount] = useState(() => getCount(props.value));
    const innerRef = useRef<SelectInstance>();

    useEffect(() => {
        setSelectedCount(getCount(props.value));
    }, [props.value]);

    useEffect(() => {
        const input = innerRef.current?.inputRef as unknown as HTMLInputElement;
        if (input) {
            input.required = selectedCount < (props.minRequired ?? 0);
        }
    }, [innerRef.current, selectedCount]);

    const onBlur = () => {
        const input = innerRef.current?.inputRef as unknown as HTMLInputElement;
        if (input) {
            input.required = selectedCount < (props.minRequired ?? 0);
        }

        props.onBlur?.();
    };

    const onChange = (val: OnChangeValue<Option<T>, TMulti>) => {
        props.onChange?.(val as (TMulti extends true ? readonly Option<T>[] : Option<T>));
        if (val) {
            setSelectedCount(Array.isArray(val) ? val.length : 1);
        } else {
            setSelectedCount(0);
        }
    };

    const setRef = (value: SelectInstance) => {
        if (ref) {
            // eslint-disable-next-line no-param-reassign
            (ref as MutableRefObject<SelectInstance>).current = value;
        }
        innerRef.current = value;
    };

    const onSortEnd = ({ oldIndex, newIndex }: { oldIndex: number, newIndex: number }) => {
        if (props.isMulti) {
            const updatedValues = arrayMove(props.value as Option<T>[], oldIndex, newIndex);
            onChange(updatedValues as unknown as OnChangeValue<Option<T>, TMulti>);
        }
    };

    type SortableType = SortableContainerProps & Props<T, TMulti> & {
        closeMenuOnSelect: boolean;
        distance: number
    } & SelectProps<Option<T>, TMulti, GroupBase<Option<T>>>
    const SortableSelect = (props.isSortable && props.isMulti)
        ? SortableContainer<SortableType>(Createable)
        : undefined;

    type MultiType = ComponentType<MultiValueProps<Option<T>, TMulti, GroupBase<Option<T>>>>
    const MultiValue = SortableSelectItem as unknown as MultiType;
    return (
        <NonceProvider cacheKey="react-select" nonce={window.NONCE}>
            {SortableSelect ? (
                <SortableSelect
                    filterOption={props.filterOption
                        ? ((row, input) => props.filterOption?.((row as FilterOptionOption<unknown>).data as T, input) ?? true)
                        : undefined}
                    axis="xy"
                    onSortEnd={onSortEnd}
                    distance={4}
                    components={{
                        MultiValue,
                    }}
                    closeMenuOnSelect={false}
                    placeholder={props.placeholder}
                    isMulti={props.isMulti}
                    onChange={val => onChange(val)}
                    onBlur={onBlur}
                    isClearable={props.isClearable}
                    options={props.options as Option<T>[]}
                    value={props.value as ((TMulti extends true ? Option<T>[] : Option<T>) & PropsValue<Option<T>>)}
                    menuPortalTarget={document.body}
                    onKeyDown={props.onKeyDown}
                    className={props.className}
                    styles={{
                        menuPortal: (base: CSSObjectWithLabel) => ({
                            ...base,
                            zIndex: 1100,
                        }),
                    }}
                />
            ) : (
                <ReactSelect
                    filterOption={props.filterOption
                        ? (
                            (row, input) => props.filterOption?.((row as FilterOptionOption<Option<T>>).data?.data as T, input)
                            ?? true
                        )
                        : undefined}
                    placeholder={props.placeholder}
                    isMulti={props.isMulti}
                    onChange={val => onChange(val)}
                    onBlur={onBlur}
                    isClearable={props.isClearable}
                    options={props.options as Option<T>[]}
                    value={props.value}
                    ref={setRef}
                    menuPortalTarget={document.body}
                    onKeyDown={props.onKeyDown}
                    className={props.className}
                    styles={{
                        menuPortal: base => ({
                            ...base,
                            zIndex: 1100,
                        }),
                    }}
                />
            )}

        </NonceProvider>
    );
});

// type SelectParams = Parameters<typeof UsersPicker>;
// const Users = React.forwardRef<SelectInstanceHandler, SelectParams[0]>(UsersPicker) as <
//     TMulti extends boolean = false,
//     TIsClearable extends boolean = false
// > (
//     props: UsersPickerProps<TMulti, TIsClearable> & { ref?: React.ForwardedRef<SelectInstanceHandler> }
// ) => ReturnType<typeof Select>;

ForwardSelect.defaultProps = {
    placeholder: "",
    isMulti: false,
    value: undefined,
    minRequired: 0,

    onChange: undefined,
    onBlur: undefined,
    className: "",
    isClearable: false,
    onKeyDown: undefined,
    isSortable: false,
    filterOption: undefined,
};

export default ForwardSelect as <
    T,
    TMulti extends boolean = false,
> (
    props: Props<T, TMulti> & { ref?: React.ForwardedRef<SelectInstanceHandler> }
) => ReturnType<typeof ForwardSelect>;
